import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { TreeSelect } from 'primeng/treeselect';
import { combineLatest, finalize, map, of, switchMap } from 'rxjs';
import { HierarchyNodeSelectDto } from '../hierarchy-node/hierarchy-node.model';
import { HierarchyType } from '../hierarchy-type.enum';
import { HierarchyService } from '../hierarchy.service';

@Component({
    selector: 'app-hierarchy-tree-select',
    imports: [TreeSelect, FormsModule],
    templateUrl: './hierarchy-tree-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HierarchyTreeSelectComponent {
    private hierarchyService = inject(HierarchyService);

    selectedHierarchyNodeKeys = model<number[]>([]);

    subscriberKey = input.required<number | null>();

    id = input<string>('hierarchyTreeSelect');
    placeholder = input<string | undefined>('Select hierarchy nodes');

    invalid = input<boolean>(false);
    disabled = input<boolean>(false);

    hierarchyType = input.required<HierarchyType>();
    selectionMode = input.required<'single' | 'checkbox' /*| 'multiple'*/>();

    selectedHierarchyNodes = signal<TreeNode[]>([]);
    selectedEntityKeys: number[] = [];
    
    /** Emits on every hierarchy selection change. */
    entityKeysChange = output<number[]>();

    /** Emits once when the selector closes and the selection is finalized. */
    entityKeysCommitted = output<number[]>();

    isLoading = signal<boolean>(false);
    loadingChange = output<boolean>();
    blur = output();

    // options = signal<TreeNode[]>([]);
    /* Reload the Options anytime the SubscriberKey or HierarchyType changes */
    options = toSignal(
        combineLatest({
            subscriberKey: toObservable(this.subscriberKey),
            hierarchyType: toObservable(this.hierarchyType)
        }).pipe(
            switchMap(({subscriberKey, hierarchyType}) => {
                /* Clear options if a Subscriber is not set */
                if(subscriberKey === null) {
                    return of([]);
                }

                this.isLoading.set(true);

                return this.hierarchyService
                    .getHierarchyTree(subscriberKey, hierarchyType)
                    .pipe(
                        map(rootNode => {
                            if (!rootNode)
                            {
                                return [];
                            }

                            return [this.getTreeNodeFromHierarchyNode(rootNode, true)]; // Options are expected to be an array. But we only have one node at the root.
                        }),
                        finalize(() => this.isLoading.set(false))
                    )
            })
        ),
        { initialValue: [] }
    );

    isDisabled = computed(() => this.isLoading() || this.disabled());
    isInvalid = computed(() => this.invalid() && !this.isDisabled());

    constructor() {
        /** Select Nodes when SelectedKeys or Options Change
         *  NOTE: Ideally handleModelChange would update the SelectedKeys Only and then this effect would always handle selecting the nodes.
         *        But unselecting the Root Node, and then setting SelectedNodes to an empty array here, basically resets the select since it's a different empty array
         *        and the Root Node automatically collapses. 
         *        So handleModelChange will set both selectedHierarchyNodes and `selectedHierarchyNodeKeys` and this effect will only update selectedHierarchyNodes if
         *        the selected Keys are different.
         */
        effect(() => {
            const selectedHierarchyNodeKeys = this.selectedHierarchyNodeKeys();
            const options = this.options();

            let selectedHierarchyNodes: TreeNode[] = [];
            untracked(() => { 
                 selectedHierarchyNodes = this.selectedHierarchyNodes();                
            });

            const selectedKeysFromNodes = selectedHierarchyNodes.map(hn => Number(hn.key));
            const areKeysEqual = selectedHierarchyNodeKeys.length === selectedKeysFromNodes.length &&
                 selectedHierarchyNodeKeys.every((k, i) => k === selectedKeysFromNodes[i]);

            if (areKeysEqual){
                 return;
            }

            if (!selectedHierarchyNodeKeys?.length || !options.length) {
                 this.selectedHierarchyNodes.set([]);
                 this.expandRootOption();
                 
                 return;
            }

            const selectedNodes = this.getSelectedNodes(selectedHierarchyNodeKeys, options)
            this.selectedHierarchyNodes.set(selectedNodes);

            /* Because the selection was not changed by the user, we also need to Emit entityKeysCommitted here. */
            const entityKeys = this.getSelectedEntityKeys(selectedNodes);
            this.entityKeysCommitted.emit(entityKeys);

        });

        
        /* Emit EntityKeysChanged whenever the SelectedNodes change */
        effect(() => {
            const selectedHierarchyNodeKeys = this.selectedHierarchyNodeKeys();
            const options = this.options();

            if (!selectedHierarchyNodeKeys.length || !options.length) {
                this.entityKeysChange.emit([]);
                return;
            }
            
            const nodes = this.getSelectedNodes(selectedHierarchyNodeKeys, options);
            const entityKeys = this.getSelectedEntityKeys(nodes);

            this.entityKeysChange.emit(entityKeys);
        })

 
		/* Loading Change */
		effect(() => {
			this.loadingChange.emit(this.isLoading());
		});
    }


    /** Recursively converts the HierarchyNode object into a TreeNode to be used by the PrimeNG Tree Select component. */
    private getTreeNodeFromHierarchyNode(hierarchyNode: HierarchyNodeSelectDto, expanded: boolean): TreeNode<HierarchyNodeSelectDto> {
        /* Sort Children by Name */
        const children = hierarchyNode.children.sort((a, b) => {
            const hierarchyNodeNameA = a.hierarchyNodeNameOverride ?? a.hierarchyNodeName;
            const hierarchyNodeNameB = b.hierarchyNodeNameOverride ?? b.hierarchyNodeName;

            return hierarchyNodeNameA.localeCompare(hierarchyNodeNameB);
        });

        return {
            key: hierarchyNode.hierarchyNodeKey.toString(),
            label: hierarchyNode.hierarchyNodeNameOverride ?? hierarchyNode.hierarchyNodeName,
            children: children.map((child) => this.getTreeNodeFromHierarchyNode(child, true)),
            expanded: expanded,
            data: hierarchyNode
        };
    }

    /** Recursively goes through the options and returns a flattened array of Selected Nodes based on the Selected Keys. */
    private getSelectedNodes(selectedKeys: number[], options: TreeNode<HierarchyNodeSelectDto>[]): TreeNode<HierarchyNodeSelectDto>[] {
        var selectedNodes: TreeNode<HierarchyNodeSelectDto>[] = [];

        options.forEach((option) => {
            if (selectedKeys.includes(option.data!.hierarchyNodeKey)) {
                selectedNodes.push(option);
            }

            const children = option.children ?? [];
            if (children.length > 0) {
                selectedNodes.push(...this.getSelectedNodes(selectedKeys, children));
            }
        });

        return selectedNodes;
    }

    handleModelChange(selectedNodes: TreeNode<HierarchyNodeSelectDto>[] | null) {
        const newKeys = selectedNodes?.map(n => Number(n.key)) ?? [];
        this.selectedHierarchyNodeKeys.set(newKeys);

        this.selectedHierarchyNodes.set(selectedNodes ?? []);

    }

    onClear() {
        this.selectedEntityKeys = [];
        this.entityKeysChange.emit([]);
        this.entityKeysCommitted.emit([]);

        this.expandRootOption();
    }

    onHide() {
        const selectedNodes = this.selectedHierarchyNodes();

        const entityKeys = this.getSelectedEntityKeys(selectedNodes);

        /* Don't Emit if nothing changed */
        if (entityKeys.length === this.selectedEntityKeys.length && entityKeys.every((v, i) => this.selectedEntityKeys.includes(v))) {
            return;
        }

        this.selectedEntityKeys = entityKeys;
        this.entityKeysCommitted.emit(entityKeys);
    }

    private getSelectedEntityKeys = (nodes: TreeNode<HierarchyNodeSelectDto>[]) => nodes.filter((n) => n.data?.entityKey !== null).map((n) => n.data!.entityKey!);

    /** I"m running into a strange behavior when clearing where:
     * 1. Component loads with a selectedHierarchyNodeKeys set by the parent.
     * 2. The selection is cleared either by the Parent or clicking the Clear button (`onClear`).
     * 3. The Root Node is collapsed.
     * 
     * Since there's only ever 1 root node it doesn't make sense to collapse it. This method will expand that node.
     */
    private expandRootOption() {
        const root = this.options()[0];
        if (root) {
            root.expanded = true;
        }
    }
}
